/**
 * Breadcrumb
 * 
 * @Author: Focci
 * @Date: 2023-08-07 10:14:17
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-07 10:14:17
 */

'use client'

import PropTypes from 'prop-types'
import Breadcrumbs from '@comp/Breadcrumbs'
import useTracking from '@hook/useTracking'
import SVGNewWindow from '@img/new-window.svg'
import Link from 'next/link'
import CloseIcon from '@mui/icons-material/Close'
import useProgressRouter from '@hook/useProgressRouter'
import { propertyDetail } from '@lib/route/house'
import { useCallback } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { getCategoryName, toSlug } from '@lib/utils'
import { houseSearchType } from '@lib/config'
import { args, modeType, urls } from '@lib/parse'
import BreadcrumbPrefix from './BreadcrumbPrefix'
import BreadcrumbAction from './BreadcrumbAction'

function getBreadcurmbOptions(house, tm, tp, locale) {
  const options = []
  const urlOption = { [modeType.listing]: [], page: 1 }
  let rds = {}

  // type
  const { type } = house
  
  if(!type) return options
  urlOption.type = type
  
  options.push({
    label: getCategoryName(type, tm),
    url: urls.listing(type, urlOption, locale)
  })

  // region
  if(house.regionName && house.regionId) {
    rds = { region: [{ 
      label: house.regionName, 
      value: house.regionId, 
      slug: toSlug(house.regionName)
    }] }

    options.push({
      label: house.regionName,
      searchParam: { type, ...urlOption, ...rds },
      url: urls.listing(type, { ...urlOption, ...rds }, locale)
    })
  }

  // district
  if(house.districtName && house.districtId) {
    rds = { district: [{ 
      label: house.districtName, 
      value: house.districtId,
      slug: toSlug(house.districtName) 
    }] }

    options.push({
      label: house.districtName,
      searchParam: { type, ...urlOption, ...rds },
      url: urls.listing(type, { ...urlOption, ...rds }, locale)
    })
  }

  // suburb
  if(house.suburbName && house.suburbId) {
    rds = { suburb: [{ 
      label: house.suburbName, 
      value: house.suburbId,
      slug: toSlug(house.suburbName) 
    }] }

    options.push({
      label: house.suburbName,
      searchParam: { type, ...urlOption, ...rds },
      url: urls.listing(type, { ...urlOption, ...rds }, locale)
    })
  }

  // bedrooms, 买房与租房与才显示
  if(house.bedrooms && house.bedrooms !== '0' && [
    houseSearchType.residential,
    houseSearchType.rental,
  ].indexOf(type) > -1) {
    const isGreaterFive = house.bedrooms >= 5
    const tmp = {
      ...rds,
      [modeType.listing]: [],
      page: 1,
      bedroom: [isGreaterFive ? 5 : house.bedrooms],
    }

    options.push({
      label: tp('bedroomsCount', { n: isGreaterFive ? '5+' : house.bedrooms }),
      searchParam: { type, ...tmp },
      url: urls.listing(type, tmp, locale)
    })
  }

  // title
  if(house.h1 || house.address) {
    options.push({
      label: house.address || house.h1,
    })
  }
  
  return options
}

function ActionButton({
  Tag = Link,
  children,
  ...props
}) {
  return (
    <Tag 
      {...props}
      className="
        hidden lg:flex items-center gap-1 hover:bg-cd1 whitespace-nowrap 
        bg-cf0 rounded-full px-2 py-1 cursor-pointer transition-colors
      "
    >
      {children}
    </Tag>
  )
}

export default function Breadcrumb({
  house = {},
  showOpenNewWindowIcon
}) {
  const locale = useLocale()
  const tm = useTranslations('menu')
  const tp = useTranslations('profile')
  const th = useTranslations('house')
  const tc = useTranslations('common')
  const options = getBreadcurmbOptions(house, tm, tp, locale)

  const { pushTrackSearch } = useTracking()
  const { routerBack } = useProgressRouter()

  const handleClick = useCallback(({ searchParam }) => {
    const opt = { ...searchParam, searchType: 'breadcrumbs' }
    const query = args.listingApi(searchParam?.type, searchParam)
    
    pushTrackSearch(opt, query, false)
  }, [pushTrackSearch])
  
  return (
    <div 
      className="
        flex items-center justify-between gap-x-2 
        max-w-full overflow-x-auto
      "
    >
      <Breadcrumbs 
        options={options} 
        showBack={false}
        prefix={<BreadcrumbPrefix house={house} />}
        onClick={handleClick}
      />
      <BreadcrumbAction house={house} />
      {showOpenNewWindowIcon && (
        <div className="flex items-center gap-2">
          <ActionButton target="_blank" href={propertyDetail(house.slug, locale)}>
            <SVGNewWindow />
            {th('openInNewWindow')}
          </ActionButton>
          <ActionButton Tag="div" onClick={routerBack}>
            <CloseIcon />
            {tc('close')}
          </ActionButton>
        </div>
      )}
    </div>
  )
}

Breadcrumb.propTypes = {
  house: PropTypes.object,
}
